import type { QueryClient } from '@tanstack/react-query'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useEffectEvent } from 'react'

import type { AuthStatus } from './app-session-state'
import { changeLanguageWithLocalResources } from '../i18n'
import { getLanguageForAuthState } from '../i18n/languageDetector'
import { setStoredIsAuthenticated } from '../utils/auth-session-storage'
import { captureException } from '../utils/sentry'
import {
  getActiveStorageScopeUserId,
  setActiveStorageScopeUserId,
} from '../utils/storage-scope'
import { getNormalizedI18nLanguage } from './app-session-config'

export function useStorageScopeSynchronization({
  authStatus,
  authUserId,
  isAuthenticated,
  isStorageScopeReady,
  nextScopeUserId,
  queryClient,
  setSyncedStorageScopeKey,
  targetStorageScopeKey,
}: {
  authStatus: AuthStatus
  authUserId: string | null
  isAuthenticated: boolean
  isStorageScopeReady: boolean
  nextScopeUserId: string | null
  queryClient: Pick<QueryClient, 'clear'>
  setSyncedStorageScopeKey: Dispatch<SetStateAction<string | null | undefined>>
  targetStorageScopeKey: string | null
}) {
  const syncStorageScopeWithAuth = useEffectEvent(async () => {
    const previousScopeUserId = await getActiveStorageScopeUserId()
    const didScopeChange = previousScopeUserId !== nextScopeUserId

    if (didScopeChange) {
      queryClient.clear()
    }

    if (
      previousScopeUserId != null &&
      previousScopeUserId !== nextScopeUserId
    ) {
      await setStoredIsAuthenticated({
        isAuthenticated: false,
        userId: previousScopeUserId,
      })
    }

    await setActiveStorageScopeUserId(nextScopeUserId)
    await setStoredIsAuthenticated({
      isAuthenticated: nextScopeUserId != null,
      userId: nextScopeUserId,
    })
  })

  const reportStorageScopeSyncError = useEffectEvent((syncError: unknown) => {
    console.error(
      'Failed to synchronize storage scope with auth state',
      syncError,
    )
    captureException(syncError, {
      feature: 'storage',
      tags: { flow: 'scope_sync', authStatus },
      extra: { authUserId: authUserId ?? 'anonymous' },
    })
  })

  useEffect(() => {
    if (authStatus === 'loading') {
      return
    }

    let isCancelled = false

    const commitSyncedStorageScope = () => {
      if (!isCancelled) {
        setSyncedStorageScopeKey(targetStorageScopeKey)
      }
    }

    void syncStorageScopeWithAuth()
      .then(() => {
        commitSyncedStorageScope()
      })
      .catch((syncError) => {
        reportStorageScopeSyncError(syncError)
        commitSyncedStorageScope()
      })

    return () => {
      isCancelled = true
    }
  }, [authStatus, setSyncedStorageScopeKey, targetStorageScopeKey])

  useEffect(() => {
    if (authStatus === 'loading' || !isStorageScopeReady) {
      return
    }

    let isCancelled = false

    async function syncLanguageWithAuthState() {
      const currentLanguage = getNormalizedI18nLanguage()
      const nextLanguage = await getLanguageForAuthState(isAuthenticated)

      if (!isCancelled && currentLanguage !== nextLanguage) {
        await changeLanguageWithLocalResources(nextLanguage)
      }
    }

    void syncLanguageWithAuthState().catch((error) => {
      console.error('Failed to synchronize language with auth state', error)
      captureException(error, {
        feature: 'i18n',
        tags: { flow: 'auth_language_sync', authStatus },
      })
    })

    return () => {
      isCancelled = true
    }
  }, [authStatus, isAuthenticated, isStorageScopeReady])
}
